import { z } from "zod";

/// URL search params for `/$workspaceSlug/payroll/setup`.
///
/// `tab` is here rather than in `useState` (S3) so a link to the Components
/// tab is a link somebody can send. `structureId` opens one structure's editor:
/// present, the Structures tab renders the editor instead of the list, so a
/// half-finished structure survives a refresh and can be handed to a colleague.
///
/// `assignments` is accepted for old links only. The assignment flow moved to
/// the Salaries page with the CTC redesign; the route body redirects there.
/// "tax" replaced "periods" and "tax-slabs" (ESS-31): they were two tabs that
/// never mentioned each other, and a workspace with slabs but no tax year had
/// bands that priced nothing. Both old values are still accepted below so a
/// bookmark or a link in somebody's notes still lands somewhere sensible.
export const PAYROLL_SETUP_TABS = ["structures", "components", "tax", "attendance"] as const;

/// Retired tab values, mapped to where their content lives now.
export const RETIRED_SETUP_TABS: Readonly<Record<string, PayrollSetupTab>> = {
  periods: "tax",
  "tax-slabs": "tax",
};

export const payrollSetupRouteSearchSchema = z
  .object({
    tab: z.enum([...PAYROLL_SETUP_TABS, "assignments", "periods", "tax-slabs"]).optional(),
    structureId: z.string().optional(),
    /// The catalog search box (S3). One param, not one per tab: only one table
    /// is on screen at a time, and "here is the link to the PF component" is a
    /// thing an admin actually wants to send.
    q: z.string().optional(),
  })
  .strict();

export type PayrollSetupRouteSearch = z.infer<typeof payrollSetupRouteSearchSchema>;
export type PayrollSetupTab = (typeof PAYROLL_SETUP_TABS)[number];

/// Which tab the URL means. A `structureId` names one structure, and structures
/// only exist on the Structures tab, so a link carrying one lands there whatever
/// `tab` says. Structures is the default: it is where the template lands and
/// where the actual work happens. The legacy `assignments` value resolves to
/// the default too; `isLegacyAssignmentsTab` is what triggers the redirect.
export function resolvePayrollSetupTab(search: PayrollSetupRouteSearch): PayrollSetupTab {
  if (search.structureId) return "structures";
  if (search.tab === undefined || search.tab === "assignments") return "structures";
  // A retired value lands on the tab that absorbed it rather than on the
  // default: somebody following an old link to "periods" wanted the tax year,
  // and dropping them on Structures would look like the page had lost it.
  const retired = RETIRED_SETUP_TABS[search.tab];
  if (retired !== undefined) return retired;
  // reason: narrowed by the two checks above to the live tab union.
  return search.tab as PayrollSetupTab;
}

export function isLegacyAssignmentsTab(search: PayrollSetupRouteSearch): boolean {
  return search.tab === "assignments" && !search.structureId;
}
